/**
 * Parts Master "Columns" toolbar button + visibility / ordering popover.
 *
 * Mirrors the PO Lines column-manager UX 1:1: a flat draggable list of every
 * known column, no group headings, native HTML5 drag-and-drop. Sticky-left
 * columns (`part_number`, `description`) render at the top of the list with
 * the checkbox disabled and no drag handle (their physical placement is
 * hard-coded into the table styling so they cannot be reordered or hidden).
 *
 * State + persistence live in the parent hook (`usePartsMasterVisibleColumns`)
 * which round-trips both visibility and order through the per-user
 * preferences API. This component is intentionally presentation-only.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Columns } from 'lucide-react';
import PartsMasterColumnRow from './PartsMasterColumnRow';
import {
  STICKY_COLUMN_IDS,
  type PartsMasterColumnDescriptor,
} from './usePartsMasterVisibleColumns';

const STICKY_SET = new Set<string>(STICKY_COLUMN_IDS);

interface PartsMasterColumnsButtonProps {
  /** Every column the user can interact with (universal + schedule + extras). */
  columns: PartsMasterColumnDescriptor[];
  /** Set of currently-visible column ids. */
  visible: Set<string>;
  /** Full ordered id list (sticky-left first, then user-arranged). */
  order: string[];
  /** Flip a single column's visibility. Sticky-left ids are no-ops. */
  onToggle: (id: string) => void;
  /** Make every known column visible. */
  onSelectAll: () => void;
  /** Restore visibility + order to the registry default. */
  onReset: () => void;
  /**
   * Move an id within the draggable slice. Indexes are 0-based positions in
   * the draggable list (sticky-left ids excluded).
   */
  onReorder: (dragIndex: number, hoverIndex: number) => void;
}

/**
 * Render the Parts Master "Columns" trigger + popover.
 *
 * Drag handlers are implemented with native HTML5 events (no @dnd-kit /
 * react-dnd) so the dropdown matches the PO Lines reference exactly.
 *
 * @param columns - Toggleable column descriptors.
 * @param visible - Currently-visible column ids.
 * @param order - Persisted ordering (sticky pinned at the head).
 * @param onToggle - Single-column visibility toggle.
 * @param onSelectAll - Show all columns.
 * @param onReset - Reset visibility + order to defaults.
 * @param onReorder - Persist a reorder of the draggable slice.
 */
export default function PartsMasterColumnsButton({
  columns,
  visible,
  order,
  onToggle,
  onSelectAll,
  onReset,
  onReorder,
}: PartsMasterColumnsButtonProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Quick lookup so we can render rows in the order the parent provides.
  const columnsById = useMemo(() => {
    const map: Record<string, PartsMasterColumnDescriptor> = {};
    for (const c of columns) map[c.id] = c;
    return map;
  }, [columns]);

  // Sticky rows are pinned at the head and excluded from drag operations.
  const stickyRows = useMemo(() => {
    const rows: PartsMasterColumnDescriptor[] = [];
    for (const id of STICKY_COLUMN_IDS) {
      const col = columnsById[id];
      if (col) rows.push(col);
    }
    return rows;
  }, [columnsById]);

  // Draggable rows in the user's chosen order, falling back to registry order
  // for any column that hasn't appeared in `order` yet.
  const draggableRows = useMemo(() => {
    const seen = new Set<string>();
    const rows: PartsMasterColumnDescriptor[] = [];
    for (const id of order) {
      if (STICKY_SET.has(id)) continue;
      const col = columnsById[id];
      if (col) {
        rows.push(col);
        seen.add(id);
      }
    }
    for (const col of columns) {
      if (!STICKY_SET.has(col.id) && !seen.has(col.id)) rows.push(col);
    }
    return rows;
  }, [columns, columnsById, order]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 ${
          open ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
        }`}
      >
        <Columns className="h-3 w-3" />Columns
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-border rounded-lg shadow-lg z-50 max-h-[400px] overflow-y-auto">
          <div className="p-2 border-b flex items-center justify-between">
            <span className="text-sm font-medium">Manage Columns</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onSelectAll}
                className="text-xs text-primary hover:underline"
              >
                Select All
              </button>
              <span className="text-xs text-muted-foreground">|</span>
              <button
                type="button"
                onClick={onReset}
                className="text-xs text-primary hover:underline"
              >
                Reset
              </button>
            </div>
          </div>
          <div className="px-2 py-1 text-xs text-muted-foreground border-b bg-muted/30">
            Drag to reorder - Click to show/hide
          </div>
          <div className="p-2 space-y-1">
            {stickyRows.map((col) => (
              <PartsMasterColumnRow
                key={col.id}
                column={col}
                visible={visible.has(col.id)}
                draggable={false}
                index={0}
                onToggle={onToggle}
                onReorder={onReorder}
              />
            ))}
            {draggableRows.map((col, index) => (
              <PartsMasterColumnRow
                key={col.id}
                column={col}
                visible={visible.has(col.id)}
                draggable
                index={index}
                onToggle={onToggle}
                onReorder={onReorder}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
